import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { ScheduleResMocks, SingleResMock } from '../types/schedule.type'

type Props = {
  staffId?: string
  startDate?: string
  endDate?: string
  sort_dir?: string
  sort_by?: string
  query?: string
}

export const useSchedule = (arg?: Props) => {
  const staff_id = arg?.staffId
  const sort_dir = arg?.sort_dir || 'desc'
  const sort_by = arg?.sort_by || 'created_at'
  const start_date = arg?.startDate
  const end_date = arg?.endDate
  const query = arg?.query
  const { data: schedules, isLoading: scheduleIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<ScheduleResMocks>('schedules', {
        params: {
          ...(sort_dir && { sort_dir }),
          ...(staff_id && { staff_id }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
          ...(sort_by && { sort_by }),
          ...(query && { query })
        }
      })
      return data
    },
    queryKey: ['schedules', { end_date, query, sort_by, sort_dir, staff_id, start_date }]
  })

  const { data: singleSchedule, isPending: scheduleLoading } = useQuery({
    enabled: !!staff_id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SingleResMock>(`schedules/${staff_id}`)
      return data
    },
    queryKey: ['schedule', { staff_id }]
  })

  const { mutateAsync: createSchedule } = useMutation({
    mutationFn: async (schedule: any) => {
      const { data } = await AXIOS_INSTANCE.post('schedules', schedule, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] })
    }
  })

  const { mutateAsync: updateSchedule } = useMutation({
    mutationFn: async ({ id, schedule }: { id: string; schedule: any }) => {
      const { data } = await AXIOS_INSTANCE.patch(`schedules/${id}`, schedule, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] })
    }
  })

  const { mutateAsync: deleteSchedule } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`schedules/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedules'] })
  })

  return {
    createSchedule,
    deleteSchedule,
    includedDatas: schedules?.included,
    scheduleIsLoading,
    scheduleLoading,
    schedules: schedules?.data,
    singleSchedule,
    updateSchedule
  }
}
